import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
  useId,
} from "react";
import Icon from "@mdi/react";
import { mdiClockOutline } from "@mdi/js";
import "./styles.scss";
import { useDateTimeContext } from "../DateTimeContext";
import { createPortal } from "react-dom";

const generateTimes = (selectedDate = null, minTime = null) => {
  const times = [];
  const now = new Date();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  const minBookingTime = currentTime + 90; // 1.5 hours from now

  const isToday = selectedDate &&
    selectedDate.getDate() === now.getDate() &&
    selectedDate.getMonth() === now.getMonth() &&
    selectedDate.getFullYear() === now.getFullYear();

  let minTimeMinutes = null;
  if (minTime) {
    const [mh, mm] = minTime.split(":").map(Number);
    if (!isNaN(mh)) minTimeMinutes = mh * 60 + (mm || 0);
  }

  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const timeInMinutes = h * 60 + m;
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      const hour12 = h % 12 || 12;
      const ampm = h < 12 ? "AM" : "PM";
      const label = `${String(hour12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;

      if (isToday && timeInMinutes <= minBookingTime) continue;
      if (minTimeMinutes !== null && timeInMinutes < minTimeMinutes) continue;

      times.push({ value, label });
    }
  }
  return times;
};

const TimePickerInput = forwardRef(
  ({ value, onChange, placeholder = "Pick Up Time", pickerId, selectedDate, minTime }, ref) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const times = generateTimes(selectedDate, minTime);

    const { activePicker, setActivePicker } = useDateTimeContext();

    const autoId = useId();
    const PICKER_ID = pickerId || `time-${autoId}`;

    useImperativeHandle(ref, () => ({
      open: () => {
        setActivePicker(PICKER_ID);
        setOpen(true);
      },
      close: () => {
        setOpen(false);
        if (activePicker === PICKER_ID) setActivePicker(null);
      },
      toggle: () => {
        if (open) {
          setOpen(false);
          setActivePicker(null);
        } else {
          setActivePicker(PICKER_ID);
          setOpen(true);
        }
      },
    }));

    // Close if another picker becomes active
    useEffect(() => {
      if (activePicker !== PICKER_ID && open) setOpen(false);
    }, [activePicker, open, PICKER_ID]);

    const handleClick = () => {
      setActivePicker(PICKER_ID);
      setOpen((p) => !p);
    };

    const selectedLabel = times.find((t) => t.value === value)?.label || placeholder;

    return (
      <div className="dtp-root" ref={rootRef}>
        <div
          ref={inputRef}
          className={`dtp-input ${value ? "" : "is-placeholder"}`}
          style={{ fontSize: "14px" }}
          role="button"
          tabIndex={0}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleClick();
          }}
        >
          <span className="dtp-input__text" style={{ fontSize: "14px" }}>{selectedLabel}</span>
          <Icon path={mdiClockOutline} size={1.2} color="#666" className="dtp-input__icon" />
        </div>

        {open &&
          rootRef.current &&
          createPortal(
            (() => {
              const rect = rootRef.current.getBoundingClientRect();

              return (
                <div
                  className="ttp-popup"
                  style={{
                    position: "absolute",
                    top: rect.bottom + window.scrollY + 8,
                    left: rect.left + window.scrollX,
                    zIndex: 99999,
                    width: rect.width,
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {times.length === 0 ? (
                    // Every slot for the selected day is inside the 90-minute
                    // lead-time window (e.g. picking "today" late in the
                    // evening), so the list would otherwise render empty and
                    // look like the picker never opened. Tell the rider why.
                    <div className="ttp-empty" style={{ padding: "14px 16px", fontSize: "13px", lineHeight: 1.5, color: "#6b7280", fontWeight: 500 }}>
                      No pickup times left for this date. Please pick a later date.
                    </div>
                  ) : (
                    times.map((t) => (
                      <div
                        key={t.value}
                        className={`ttp-option ${value === t.value ? "selected" : ""}`}
                        onClick={() => {
                          onChange(t.value);
                          setTimeout(() => {
                            setOpen(false);
                            setActivePicker(null);
                          }, 120);
                        }}
                      >
                        {t.label}
                      </div>
                    ))
                  )}
                </div>
              );
            })(),
            document.body
          )}
      </div>
    );
  }
);

export default TimePickerInput;
